import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusCircle, Eye, EyeOff, Edit, UserX, Users, UserCheck, Clock, LogOut } from "react-feather";
import Container from "../../../components/ui/Container";
import MainTable from "../../../components/tables/MainTable";
import MainModal from "../../../components/modals/MainModal";
import ExportButtons from "../../../components/ui/ExportButtons";
import { notify } from "../../../components/ui/Toast&Alerts";
import { DateFormat, customStyleSm } from "../../../utils/common";
import Select from "react-select";
import { hrApi, useHrScope } from "../hrApi";
import { StatusBadge, StatCard, DealerFilter, SelectFilter, useHrMasters, fmtAmount, BusyLabel, RefreshButton, StatRow } from "../components/HrCommon";
import { EMPLOYEE_STATUS, EXIT_STATUSES, toOptions, todayIso, toIsoDate } from "../hrConstants";
import { all_routes } from "../../../Router/all_routes";

const statusFilterOptions = [{ value: "Current", label: "Current Employees" }, ...toOptions(EMPLOYEE_STATUS)];

const EmployeeList = () => {
    const navigate = useNavigate();
    const { isDistributor, defaultDealerId } = useHrScope();
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [filters, setFilters] = useState({ dealerId: defaultDealerId, departmentId: "", designationId: "", status: "Current", search: "" });
    // Designations belong to a dealer's HR setup, so the filter needs a dealer
    const masters = useHrMasters(["departments", "designations"], filters.dealerId);
    const [statusModal, setStatusModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const [noticeDays, setNoticeDays] = useState(30);   // Payroll Settings: notice period

    useEffect(() => { load(); }, []);
    useEffect(() => {
        hrApi.getSettings(filters.dealerId).then((r) => r?.isSuccess && setNoticeDays(Number(r.data.noticePeriodDays) || 0));
    }, [filters.dealerId]);

    // Resigning / going on notice: last working date defaults to today + notice period
    const changeModalStatus = (status) => setStatusModal((m) => ({
        ...m, status,
        exitDate: m.exitDate || (["On Notice", "Resigned"].includes(status) ? toIsoDate(new Date(Date.now() + noticeDays * 86400000)) : ""),
    }));

    const load = async (f = filters) => {
        setLoading(true);
        const res = await hrApi.employees(f);
        setList(res?.isSuccess ? res.data : []);
        if (!res?.isSuccess) notify("error", res?.message);
        setLoading(false);
    };

    const openProfile = (row) => navigate(`${all_routes.hrEmployeeProfile}${row ? `?id=${row.employeeId}` : ""}`);

    const saveStatus = async () => {
        const m = statusModal;
        if (!m.status) return notify("warning", "Select a status");
        if (["On Notice", ...EXIT_STATUSES].includes(m.status) && !m.exitDate) return notify("warning", "Last working date is required");
        setSaving(true);
        const res = await hrApi.changeEmployeeStatus({ employeeId: m.employeeId, status: m.status, exitDate: m.exitDate, exitReason: m.exitReason });
        setSaving(false);
        notify(res?.isSuccess ? "success" : "error", res?.message);
        if (res?.isSuccess) { setStatusModal(null); load(); }
    };

    const columns = [
        { title: "Code", dataIndex: "employeeCode", exportValue: (v) => v },
        { title: "Employee", dataIndex: "fullName", render: (v, r) => <a role="button" className="text-primary" onClick={() => openProfile(r)}>{v}</a>, exportValue: (v) => v },
        { title: "Designation", dataIndex: "designationName" },
        { title: "Department", dataIndex: "departmentName" },
        ...(isDistributor ? [{ title: "Dealer", dataIndex: "dealerName" }] : []),
        { title: "Type", dataIndex: "employmentType" },
        { title: "Joining", dataIndex: "joiningDate", render: (v) => DateFormat(v), exportValue: (v) => DateFormat(v) },
        { title: "Mobile", dataIndex: "mobileNo" },
        { title: "Basic Salary", dataIndex: "basicSalary", align: "right", numeric: true, render: fmtAmount },
        { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} />, exportValue: (v) => v },
        {
            title: "Actions", key: "act", exportable: false, render: (_, r) => (
                <div className="edit-delete-action">
                    <button type="button" className="mybtn" title="Open profile" onClick={() => openProfile(r)}><Edit className="feather-edit31" /></button>
                    <button type="button" className="mybtn" title="Change status" onClick={() => setStatusModal({ ...r, exitDate: r.exitDate || "", exitReason: r.exitReason || "" })}><UserX className="feather-trash21" /></button>
                </div>
            ),
        },
    ];

    const count = (fn) => list.filter(fn).length;

    return (
        <Container
            pageName="Employees"
            showBackButton
            pageDescription="Employee master for HR & Payroll"
            headerExtra={
                <div className="d-flex gap-2">
                    <ExportButtons columns={columns} data={[...list].reverse()} getMeta={() => ({ title: "Employee List", fileName: "Employees", filters: [{ label: "Status", value: filters.status }] })} />
                    <RefreshButton onClick={() => load()} loading={loading} />
                    <button className="btn btn-added" onClick={() => setShowFilters(!showFilters)}>
                        {showFilters ? <><EyeOff size={14} className="me-2" />Hide Filters</> : <><Eye size={14} className="me-2" />Show Filters</>}
                    </button>
                    <button className="btn btn-added" onClick={() => openProfile(null)}><PlusCircle className="me-2" />Add Employee</button>
                </div>
            }
        >
            <StatRow>
                <div className="col-6 col-md-3"><StatCard label="Listed" value={list.length} color="#2563eb" icon={<Users />} /></div>
                <div className="col-6 col-md-3"><StatCard label="Active" value={count((e) => e.status === "Active")} color="#16a34a" icon={<UserCheck />} /></div>
                <div className="col-6 col-md-3"><StatCard label="On Probation" value={count((e) => e.status === "Probation")} color="#0891b2" icon={<Clock />} /></div>
                <div className="col-6 col-md-3"><StatCard label="On Notice" value={count((e) => e.status === "On Notice")} color="#d97706" icon={<LogOut />} /></div>
            </StatRow>
            <div className="card table-list-card">
                <div className="card-body">
                    {showFilters && (
                        <div className="form-row-custom p-2">
                            <DealerFilter value={filters.dealerId} onChange={(v) => setFilters({ ...filters, dealerId: v, designationId: "" })} />
                            <SelectFilter label="Department" className="field-180" options={masters.departments} isLoading={masters.loading} value={filters.departmentId} onChange={(v) => setFilters({ ...filters, departmentId: v })} />
                            {filters.dealerId > 0 && <SelectFilter label="Designation" className="field-180" options={masters.designations} isLoading={masters.loading} value={filters.designationId} onChange={(v) => setFilters({ ...filters, designationId: v })} />}
                            <SelectFilter label="Status" className="field-180" options={statusFilterOptions} value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} placeholder="All Statuses" />
                            <div className="field-220">
                                <label className="form-label">Search</label>
                                <input className="form-control" placeholder="Name, code, CNIC, mobile" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} />
                            </div>
                            <div className="field-auto d-flex justify-content-end align-items-end">
                                <button className="btn btn-secondary me-2" onClick={() => { const f = { dealerId: defaultDealerId, departmentId: "", designationId: "", status: "Current", search: "" }; setFilters(f); load(f); }}>Reset</button>
                                <button className="btn btn-primary" onClick={() => load()} disabled={loading}><BusyLabel busy={loading} text="Searching...">Search</BusyLabel></button>
                            </div>
                        </div>
                    )}
                    <MainTable
                        columns={columns}
                        dataSource={[...list].reverse()}
                        loading={loading}
                        options={{ withActions: false, bordered: true, size: "small", rowKey: "employeeId", scroll: { x: "max-content" }, emptyText: "No employees found" }}
                    />
                </div>
            </div>

            <MainModal
                page={`Change Status - ${statusModal?.fullName || ""}`}
                size="md"
                show={!!statusModal}
                modalshow={() => setStatusModal(null)}
                modalsave={saveStatus}
                loading={saving}
            >
                {statusModal && (
                    <div className="row">
                        <div className="col-12 mb-2 text-muted" style={{ fontSize: 12 }}>
                            Current status: <StatusBadge status={statusModal.status} />. To settle dues of an exiting employee use Final Settlement.
                        </div>
                        <div className="col-6 mb-3">
                            <label className="form-label">*New Status</label>
                            <Select styles={customStyleSm} options={toOptions(EMPLOYEE_STATUS)} value={toOptions(EMPLOYEE_STATUS).find((o) => o.value === statusModal.status) || null} onChange={(o) => changeModalStatus(o?.value)} />
                        </div>
                        <div className="col-6 mb-3">
                            <label className="form-label">Last Working Date</label>
                            <input type="date" className="form-control" min={statusModal.joiningDate} value={statusModal.exitDate} onChange={(e) => setStatusModal({ ...statusModal, exitDate: e.target.value })} disabled={["Active", "Probation"].includes(statusModal.status)} />
                        </div>
                        <div className="col-12 mb-2">
                            <label className="form-label">Reason / Remarks</label>
                            <textarea className="form-control" rows={2} value={statusModal.exitReason} onChange={(e) => setStatusModal({ ...statusModal, exitReason: e.target.value })} placeholder={`e.g. Resignation received on ${DateFormat(todayIso())}`} />
                        </div>
                    </div>
                )}
            </MainModal>
        </Container>
    );
};

export default EmployeeList;
